import { Component, OnDestroy, OnInit } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { TranslateService } from "@ngx-translate/core";
import { Subject, takeUntil } from "rxjs";
import { AuthService } from "../../services/auth.service";
import { ConnectionEventsService } from "../../services/connection-events.service";
import { LanguageService } from "../../services/language.service";
import { SessionService } from "../../services/session.service";
import { UserService } from "../../services/user.service";

@Component({
  selector: "app-account-menu",
  templateUrl: "./account-menu.component.html",
  styleUrls: ["./account-menu.component.scss"],
})
export class AccountMenuComponent implements OnInit, OnDestroy {
  public languages: string[] = [];
  private onDestroy$ = new Subject<void>();

  /**
   * @return The currently active language
   */
  public get currentLang(): string {
    return this.translateService.currentLang;
  }

  /**
   * Switches the application language and persists it for the current session, also persisting
   * it on the user's account when logged in so it is used for future logins and emails.
   * @param value Language code to switch to
   */
  public set currentLang(value: string) {
    this.translateService.use(value);
    this.sessionService.userLang = value;

    if (this.sessionService.isLoggedIn) {
      this.userService
        .changeLanguage(value)
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({
          error: () => {
            this.snackBar.open(`❗ ${this.translateService.instant("common.error")}`, "OK", {
              duration: 5000,
            });
          },
        });
    }
  }

  constructor(
    private translateService: TranslateService,
    public sessionService: SessionService,
    private authService: AuthService,
    private languageService: LanguageService,
    private userService: UserService,
    private connectionEventsService: ConnectionEventsService,
    private snackBar: MatSnackBar
  ) {}

  /**
   * Loads available languages.
   */
  ngOnInit(): void {
    this.languageService
      .getLanguages()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((res) => {
        this.languages = res.map((c) => c.code);
      });
  }

  /**
   * Emits onDestroy$ to terminate all active subscriptions.
   */
  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  /**
   * Logs the current user out and notifies the application of the logout, regardless of whether
   * the logout request succeeds.
   */
  public logout(): void {
    if (this.sessionService.isLoggedIn) {
      this.authService
        .logout()
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({
          next: () => {
            this.connectionEventsService.notifyLogout();
          },
          error: () => {
            this.snackBar.open(`❗ ${this.translateService.instant("common.error")}`, "OK", {
              duration: 5000,
            });
            this.connectionEventsService.notifyLogout();
          },
        });
    }
  }
}
